import React, { useCallback } from 'react';
import { Panel, useReactFlow } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { IconProvider } from '../../../ui';

export interface BulkEdgeStyleControlsProps {
  /** Position of the panel (only used if withPanel is true) */
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  /** Whether to wrap in a Panel component (default: true) */
  withPanel?: boolean;
}

const edgeStyles = [
  { type: 'default', label: 'Default (Bezier)', icon: 'fa-wave-sine' },
  { type: 'straight', label: 'Straight', icon: 'fa-minus' },
  { type: 'step', label: 'Step', icon: 'fa-stairs' },
  { type: 'smoothstep', label: 'Smooth Step', icon: 'fa-chart-line-up' },
  { type: 'simplebezier', label: 'Simple Bezier', icon: 'fa-wave-pulse' },
];

/**
 * Bulk edge style controls for React Flow
 * Provides a button to change the style of all edges at once
 */
export function BulkEdgeStyleControls({ position = 'top-right', withPanel = true }: BulkEdgeStyleControlsProps) {
  const { setEdges, getEdges } = useReactFlow();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleBulkStyleChange = useCallback(async (
    newType: string,
    styleName: string
  ) => {
    console.log('Bulk edge style change triggered:', newType);
    
    // Close the menu
    setIsOpen(false);
    
    // Get all edges
    const edges = getEdges();
    
    if (edges.length === 0) {
      toast.error('No edges to style', {
        description: 'Add some connections to the canvas first',
      });
      return;
    }

    try {
      // Update all edges with the new type
      const updatedEdges = edges.map((edge) => ({
        ...edge,
        type: newType,
      }));

      setEdges(updatedEdges);

      toast.success('Edge style applied', {
        description: `Applied ${styleName} style to ${edges.length} edge${edges.length === 1 ? '' : 's'}`,
      });
    } catch (error) {
      console.error('Error applying edge style:', error);
      toast.error('Failed to apply edge style', {
        description: 'An error occurred while changing edge styles',
      });
    }
  }, [setEdges, getEdges]);

  const handleToggleAnimated = useCallback(() => {
    console.log('Toggle animated triggered');
    
    // Close the menu
    setIsOpen(false);
    
    // Get all edges
    const edges = getEdges();
    
    if (edges.length === 0) {
      toast.error('No edges to animate', {
        description: 'Add some connections to the canvas first',
      });
      return;
    }

    try {
      // Check if any edge is animated
      const hasAnimated = edges.some(edge => edge.animated);
      
      // Toggle all edges
      const updatedEdges = edges.map((edge) => ({
        ...edge,
        animated: !hasAnimated,
      }));

      setEdges(updatedEdges);

      toast.success(hasAnimated ? 'Animation disabled' : 'Animation enabled', {
        description: `${hasAnimated ? 'Disabled' : 'Enabled'} animation on ${edges.length} edge${edges.length === 1 ? '' : 's'}`,
      });
    } catch (error) {
      console.error('Error toggling animation:', error);
      toast.error('Failed to toggle animation', {
        description: 'An error occurred while changing edge animation',
      });
    }
  }, [setEdges, getEdges]);

  const content = (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="iconButton"
        >
          <IconProvider iconstring="fa-bezier-curve" style="light" size={16}/>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Edge Style (All Edges)</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Edge style options */}
        {edgeStyles.map((style) => (
          <DropdownMenuItem 
            key={style.type}
            onSelect={() => handleBulkStyleChange(style.type, style.label)}
          >
            <IconProvider iconstring={style.icon} style="light" size={16} className="mr-2"/>
            {style.label}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        {/* Toggle animation */}
        <DropdownMenuItem onSelect={handleToggleAnimated}>
          <IconProvider iconstring="fa-bolt" style="light" size={16}/>
          Toggle Animation
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return withPanel ? (
    <Panel position={position} className="m-2">
      {content}
    </Panel>
  ) : content;
}
